import { readJson } from "@/app/_utils/apiJson";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Text,
  NumberInput,
  Stack,
  Button,
  Group,
  TextInput,
  Collapse,
  Paper,
  Select,
  Textarea,
  ScrollArea,
  Card,
  Alert,
  Checkbox,
  Grid,
  Divider,
  SelectProps,
  Tabs,
  Switch,
  Loader,
  useComputedColorScheme,
  Box,
} from "@mantine/core";
import {
  Elements,
  CardElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { getPlatformStripePromise, loadStripe } from "@/app/_utils/stripeClient";
import { useForm } from "@mantine/form";
import { DatePickerInput } from "@mantine/dates";
import { JobPayment, JobPaymentResult, JobV2 } from "@/app/@types/job";
import { NamedUserUID, PaymentStatus, PaymentType } from "@/app/@types/enums";
import { processJobPayment } from "@/app/_helpers/processJobPayment";
import useFormatCurrency from "@/app/_hooks/useFormatCurrency";
import {
  calculateAddition,
  calculateDisplayRemainingBalance,
  calculateJob,
  isGreaterThan,
  isLessThan,
} from "@/app/_helpers/math";
import { useUserContext } from "@/app/_hooks/useUserContext";
import { isServiceLineItem } from "@/app/_helpers/lineItem";
import { UserProcessingFee } from "@/app/@types/user";
import { generateUid } from "@/app/_firebase/general";
import { IconCheck, IconCreditCardPay } from "@tabler/icons-react";
import {
  useTerminalPayment,
  STRIPE_CARD_DECLINE_MESSAGES,
} from "@/app/_hooks/useTerminalPayment";
import {
  useStripeTerminalContext,
  useHoldReaderLock,
  ReaderAcquireFailure,
  readerFailureMessage,
} from "@/app/_providers/StripeTerminalProvider";
import ReaderPicker from "@/app/_components/ReaderPicker/ReaderPicker";
import PaymentNotice from "@/app/_components/Payment/PaymentNotice/PaymentNotice";
import ReaderSummary from "@/app/_components/ReaderPicker/ReaderSummary";
import ReaderFailureNotice from "@/app/_components/ReaderPicker/ReaderFailureNotice";
import { canChargeCards } from "@/app/_helpers/user";

import { authenticatedFetch } from "@/app/_utils/authenticatedFetch";
import { containsCreditCardNumber } from "@/app/_helpers/creditCardValidation";
import useFeatureFlag from "@/app/_hooks/useFeatureFlag";
import useDeviceType from "@/app/_hooks/useDeviceType";
import { Timestamp } from "firebase/firestore";


// This form also renders inside the POS modal, which sits at a huge z-index
// so it clears the floating POS button. Dropdowns at Mantine's default
// z-index open behind that modal and can't be picked, so lift them to match.
const POPOVER_Z_INDEX = 20000000000;

interface PaymentEntryFormProps {
  job: JobV2;
  onCancel: () => void;
  onSubmit: (paymentResult: JobPaymentResult) => void;
  currency: Currency;
  mode?: "traditional" | "initial";
  isCollectingInitialPayment?: boolean;
  setIsCollectingInitialPayment?: (isCollectingInitialPayment: boolean) => void;
  withoutJobTitle?: boolean;
  // The host modal gives the form a bounded, flex-column body (full screen on
  // phones): the form fills it, the fields scroll inside, and the buttons
  // stay pinned. Without it the content is capped at 75dvh as before.
  fillHeight?: boolean;
  paymentStatusLabelOverride?: PaymentStatus | null;
  // Keeps the submit buttons spinning after the charge succeeds while the
  // caller persists the job update, so the button can't be pressed twice in
  // the gap between charge completion and modal close.
  isFinalizing?: boolean;
}

const PaymentEntryFormInner = ({
  job,
  paymentStatusLabelOverride,
  onCancel,
  onSubmit,
  mode = "traditional",
  isCollectingInitialPayment,
  setIsCollectingInitialPayment,
  withoutJobTitle = false,
  fillHeight = false,
  isFinalizing = false,
}: PaymentEntryFormProps) => {
  const user = useUserContext();
  const { isMobile } = useDeviceType();
  const stripe = useStripe();
  const elements = useElements();
  const formatCurrency = useFormatCurrency();
  // Rollout flag AND a live Stripe account with charges enabled. The flag on
  // its own is true for every tenant, so it cannot decide whether the Charge
  // Card section shows or defaults on.
  const paymentsEnabled =
    useFeatureFlag("MM_PAYMENTS_ENABLED") && canChargeCards(user);
  const colorScheme = useComputedColorScheme();
  // Nothing connects until Process is pressed: the hook takes the reader for
  // one payment and lets it go. isTerminalReady only means a reader can be
  // reached for (this device's pick, or the shop's only reader).
  const {
    processTerminalPayment,
    isTerminalReady,
    cancelTerminalPayment,
    isProcessing,
    canCancel,
  } = useTerminalPayment();

  const { hasMultipleReaders, mustPickReader, readers } =
    useStripeTerminalContext();

  // The card was charged and the host is saving it to the job: the payment
  // modal stays shut until that save lands, or a failed save would go unseen.
  useHoldReaderLock(isFinalizing);

  // Payment error state
  const [paymentError, setPaymentError] = useState<{
    error: string;
    cardDeclined: boolean;
  } | null>(null);

  // Reader could not be taken for the payment (busy on another device, not
  // found, offline). Shown inline; Process is the retry.
  const [readerFailure, setReaderFailure] = useState<{
    failure: ReaderAcquireFailure;
    error: string;
  } | null>(null);

  // Which reader this device uses. Only shops with more than one registered
  // reader ever see the picker or the Change reader link; everyone else uses
  // the shop's reader exactly as before.
  const [showPicker, setShowPicker] = useState(false);
  const [pickerNotice, setPickerNotice] = useState<string | null>(null);
  const pickerOpen = mustPickReader || (hasMultipleReaders && showPicker);
  // The reader is the way to charge here: one resolves, or the picker is
  // waiting for this device to choose one.
  const readerAvailable = isTerminalReady || mustPickReader;

  // Card charging state. On by default whenever this account can charge
  // cards: through the reader when one is connected, otherwise by typing the
  // card in. Off only records a card taken elsewhere, the rarer case.
  const cardChargingAllowed =
    paymentsEnabled && user.uid !== NamedUserUID.Mesquite;
  const [enableCardCharging, setEnableCardCharging] =
    useState(cardChargingAllowed);
  // Land on Manual Entry when no reader can be reached for, so a card can
  // still be typed in. The reader tab takes over once one is resolvable
  // unless the user has already picked a tab themselves.
  const [activeTab, setActiveTab] = useState<"reader" | "manual">(
    readerAvailable && readers.length > 0 ? "reader" : "manual"
  );
  const userPickedTab = useRef(false);

  // When a reader becomes resolvable after the form opened (the reader list
  // landing, or a pick in the picker), flip card charging on: the initial
  // useState only captured the earlier state. Manual toggling afterwards
  // still wins because this only fires when isTerminalReady changes.
  useEffect(() => {
    if (readerAvailable && readers.length > 0) {
      setEnableCardCharging(true);
      if (!userPickedTab.current) setActiveTab("reader");
    }
  }, [readerAvailable, readers.length]);

  // Cross-currency disclosure: when the merchant terminal currency differs
  // from the job currency, fetch a preview rate so we can show what will
  // actually be charged on the reader before they hit Process.
  const merchantCurrency = (user.settings.currency || "USD").toUpperCase();
  const jobCurrencyCode = job.client.currency.code.toUpperCase();
  const fxEnabled =
    user.uid === NamedUserUID.ALLOVER || user.uid === NamedUserUID.ADMIN;
  const showFxDisclosure =
    fxEnabled && merchantCurrency !== jobCurrencyCode;
  const [previewFxRate, setPreviewFxRate] = useState<number | null>(null);
  const [previewFxError, setPreviewFxError] = useState<string | null>(null);

  useEffect(() => {
    if (!showFxDisclosure) {
      setPreviewFxRate(null);
      setPreviewFxError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch(
          `/api/fx-rate?from=${jobCurrencyCode}&to=${merchantCurrency}`
        );
        if (!res.ok) throw new Error("Could not fetch FX rate");
        const data = (await readJson(res)) as { rate: number };
        if (!cancelled) {
          setPreviewFxRate(data.rate);
          setPreviewFxError(null);
        }
      } catch (err: any) {
        if (!cancelled) {
          setPreviewFxRate(null);
          setPreviewFxError(err.message || "Could not fetch FX rate");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showFxDisclosure, jobCurrencyCode, merchantCurrency]);

  // Processing fee. Look up the standard fee name first, then fall back to
  // the structured applicableTo flags for accounts with custom fee names.
  const defaultTerminalFee = useMemo(() => {
    if (
      !readerAvailable ||
      user.uid === NamedUserUID.Mesquite ||
      user.uid === NamedUserUID.ALLOVER
    )
      return null;
    const fees = user.settings.processingFees;
    return (
      fees.find(
        (fee) => fee.name === "Processing Fee - Card Reader Domestic Card"
      ) ||
      fees.find(
        (fee) =>
          fee.applicableTo?.terminal === true &&
          fee.applicableTo?.paymentType === "card_domestic"
      ) ||
      fees.find((fee) => fee.applicableTo?.terminal === true) ||
      null
    );
  }, [readerAvailable, user.uid, user.settings.processingFees]);

  const defaultManualFee = useMemo(() => {
    if (
      user.uid === NamedUserUID.Mesquite ||
      user.uid === NamedUserUID.ALLOVER
    )
      return null;
    const fees = user.settings.processingFees;
    return (
      fees.find(
        (fee) => fee.name === "Processing Fee - Online Domestic Card"
      ) ||
      fees.find(
        (fee) =>
          fee.applicableTo?.portal === true &&
          fee.applicableTo?.paymentType === "card_domestic"
      ) ||
      fees.find((fee) => fee.applicableTo?.portal === true) ||
      null
    );
  }, [user.uid, user.settings.processingFees]);

  const [selectedProcessingFee, setSelectedProcessingFee] =
    useState<UserProcessingFee | null>(defaultTerminalFee);

  // Manual card state
  const [isProcessingManual, setIsProcessingManual] = useState(false);
  const [manualCardState, setManualCardState] = useState({
    complete: false,
    error: null as string | null,
  });

  const [isOutsideOfDeposit, setIsOutsideOfDeposit] = useState<boolean>(false);
  const [remainingBalanceCustomerFacing, setRemainingBalanceCustomerFacing] =
    useState<number>(
      calculateDisplayRemainingBalance(
        job.lineItems,
        job.subtotalAfterDiscounts,
        job.totalTax,
        job.grandTotal,
        job.payments,
        job.remainingBalance
      )
    );
  const [originalPaymentType, setOriginalPaymentType] = useState<PaymentType>(
    job.paymentStatus === PaymentStatus.PaidInFull
      ? PaymentType.Full
      : job.paymentStatus === PaymentStatus.DepositReceived
      ? PaymentType.Deposit
      : job.paymentStatus === PaymentStatus.AwaitingDeposit
      ? PaymentType.Deposit
      : PaymentType.Partial
  );

  const totalPaid = useMemo(
    () => calculateJob.totalPayments(job.payments).raw(),
    [job.payments]
  );

  const calculateInitialAmount = useCallback(() => {
    if (mode === "initial") {
      return job.contract.paymentSchedule[0].amount;
    }

    switch (job.paymentStatus) {
      case PaymentStatus.PaidInFull:
        return isLessThan(totalPaid, job.grandTotal)
          ? remainingBalanceCustomerFacing
          : 0;
      case PaymentStatus.PartiallyPaid:
        return null;
      default:
        return job.totalDeposit;
    }
  }, [
    job.paymentStatus,
    totalPaid,
    job.grandTotal,
    remainingBalanceCustomerFacing,
    job.totalDeposit,
  ]);

  const calculateInitialProcessingFee = useCallback(() => {
    return selectedProcessingFee
      ? calculateJob
          .processingFeeForPayment(
            calculateInitialAmount(),
            selectedProcessingFee?.rate || 0,
            selectedProcessingFee?.fixed || 0
          )
          .raw()
      : null;
  }, [selectedProcessingFee]);

  const form = useForm<JobPayment>({
    initialValues: {
      uid: generateUid(),
      processingFee: calculateInitialProcessingFee(),
      tax: null,
      ...(() => {
        const initialAmount = calculateInitialAmount();
        return {
          amount: initialAmount,
          total: calculateAddition(
            calculateInitialProcessingFee(),
            initialAmount
          ),
        };
      })(),
      date: new Date(),
      type: mode === "traditional" ? originalPaymentType : PaymentType.Partial,
      tender:
        mode === "initial" &&
        job.contract.paymentSchedule[0].method !== "terminal"
          ? job.contract.paymentSchedule[0].method.charAt(0).toUpperCase() +
            job.contract.paymentSchedule[0].method.slice(1)
          : "Credit Card",
      notes: null,
    },
    validate: {
      amount: (value) => {
        if (!value && form.isTouched("amount")) {
          return "Amount is required";
        }

        if (!isGreaterThan(value, 0) && value !== null) {
          return "Amount should be greater than 0";
        }
        if (isGreaterThan(value, remainingBalanceCustomerFacing)) {
          return "Amount should be equal to or less than the remaining balance";
        }

        return null;
      },
      notes: (value) => {
        if (containsCreditCardNumber(value)) {
          return "Card numbers cannot be saved in notes. For secure card storage, please contact Support about MountMonitor Payments (PCI-compliant).";
        }
        return null;
      },
    },
  });

  // Process charges the current form: it needs a valid amount and a reader
  // chosen.
  const canRunReaderPayment =
    form.isValid() &&
    form.values.amount !== null &&
    form.values.amount > 0 &&
    !pickerOpen;

  useEffect(() => {
    form.validateField("amount");
    // Key off the SELECTED fee, not the computed fee amount — on jobs where
    // the amount starts empty (e.g. Partially Paid) the auto-selected fee is
    // initially computed as 0, and gating on that value meant the fee never
    // recalculated once the user typed an amount.
    if (selectedProcessingFee && isGreaterThan(form.values.amount, 0)) {
      const processingFeeAmount = calculateJob.processingFeeForPayment(
        form.values.amount,
        selectedProcessingFee?.rate || 0,
        selectedProcessingFee?.fixed || 0
      );

      const taxAmount = calculateJob.taxForProcessingFee(
        processingFeeAmount.raw(),
        job.taxRate,
        selectedProcessingFee?.taxable || false,
        job.includeTax
      );

      const processingFeeAmountWithTax = calculateAddition(
        processingFeeAmount.raw(),
        taxAmount.raw()
      );

      const finalPaymentAmount = calculateAddition(
        processingFeeAmountWithTax,
        form.values.amount
      );

      form.setFieldValue("processingFee", processingFeeAmount.raw());
      form.setFieldValue("tax", taxAmount.raw() || null);
      form.setFieldValue("total", finalPaymentAmount);
    } else {
      if (!selectedProcessingFee) {
        form.setFieldValue("processingFee", null);
        form.setFieldValue("tax", null);
      }
      form.setFieldValue("total", form.values.amount);
    }

    // test partial payment
    if (form.values.amount === remainingBalanceCustomerFacing) {
      form.setFieldValue("type", PaymentType.Full);
    } else if (
      originalPaymentType === PaymentType.Deposit &&
      (isGreaterThan(form.values.amount, job.totalDeposit) ||
        isLessThan(form.values.amount, job.totalDeposit))
    ) {
      form.setFieldValue("type", PaymentType.Partial);
      setIsOutsideOfDeposit(true);
    } else {
      if (isOutsideOfDeposit) {
        setIsOutsideOfDeposit(false);
      }
      form.setFieldValue("type", originalPaymentType);
    }
  }, [form.values.processingFee, form.values.amount, selectedProcessingFee]);

  // Handle tender changes
  useEffect(() => {
    const isCardPayment =
      form.values.tender === "Credit Card" ||
      form.values.tender === "Debit Card";

    if (!isCardPayment) {
      form.setFieldValue("processingFee", null);
      setSelectedProcessingFee(null);
      setEnableCardCharging(false);
      setActiveTab(readerAvailable && readers.length > 0 ? "reader" : "manual");
      userPickedTab.current = false;
      setManualCardState({ complete: false, error: null });
      return;
    }

    // Card selected: charge it, through the reader when one is connected and
    // by manual entry otherwise, with the matching fee.
    if (!cardChargingAllowed) return;
    setEnableCardCharging(true);
    if (!userPickedTab.current) {
      setActiveTab(readerAvailable && readers.length > 0 ? "reader" : "manual");
    }
    const fee = readerAvailable ? defaultTerminalFee : defaultManualFee;
    if (fee && form.values.amount) {
      const calculatedFee = calculateJob
        .processingFeeForPayment(
          form.values.amount,
          fee.rate || 0,
          fee.fixed || 0
        )
        .raw();

      form.setFieldValue("processingFee", calculatedFee);
      setSelectedProcessingFee(fee);
    }
  }, [form.values.tender, defaultTerminalFee, defaultManualFee]);

  // Handle tab changes - switch processing fee between terminal and manual.
  // Selected before any amount is typed, the same as the reader fee is on
  // open, so the form shows which fee will apply. The amount effect above
  // computes the actual fee once there is an amount. Not keyed on the amount,
  // so a hand-picked fee survives typing.
  useEffect(() => {
    const isCardPayment =
      form.values.tender === "Credit Card" ||
      form.values.tender === "Debit Card";

    if (!isCardPayment || !enableCardCharging) return;

    const feeToUse =
      activeTab === "reader" ? defaultTerminalFee : defaultManualFee;

    if (feeToUse) setSelectedProcessingFee(feeToUse);
  }, [activeTab, defaultTerminalFee, defaultManualFee, enableCardCharging]);

  useEffect(() => {
    form.validateField("notes");
  }, [form.values.notes]);

  useEffect(() => {
    if (form.values.amount === null) {
      form.resetDirty();
    }
  }, [form.values.amount]);

  // Terminal payment handler. Takes the reader, charges, lets it go.
  const runTerminalPayment = async () => {
    if (!isTerminalReady || !canRunReaderPayment) return;

    setPaymentError(null);
    setReaderFailure(null);

    const result = await processTerminalPayment(
      user.uid,
      {
        jobUid: job.uid,
        jobNum: job.jobId,
        client: job.client,
        amount: form.values.amount,
        processingFee: form.values.processingFee,
        ...(job.contract?.hasPaymentPlan ? { outOfBandPayment: true } : {}),
      }
    );

    if (result.success) {
      const terminalPayment: JobPayment = {
        ...form.values,
        date: Timestamp.fromDate(form.values.date as Date),
        // `?? null` added 25 Oct 2026 after Critters Creation Taxidermy's
        // reader returned an undefined charge id: Firestore rejected the job
        // write AFTER the card was charged (twice, job #17, one refunded by
        // hand). null is safe, refunds go by stripePaymentIntentId. Keep it
        // unless the root cause is found. Full story in useTerminalPayment,
        // above the latestCharge extraction.
        stripeTransactionId: result.stripeTransactionId ?? null,
        stripePaymentIntentId: result.stripePaymentIntentId,
        ...(result.fxRate ? { fxRate: result.fxRate } : {}),
        ...(result.processedAmount !== undefined
          ? { processedAmount: result.processedAmount }
          : {}),
        ...(result.processedCurrency
          ? { processedCurrency: result.processedCurrency }
          : {}),
      };

      const data = processJobPayment(
        user,
        job,
        terminalPayment,
        selectedProcessingFee || null
      );

      if (setIsCollectingInitialPayment !== undefined) {
        setIsCollectingInitialPayment(true);
      }

      onSubmit(data);
      return;
    }

    // Staff pressed Cancel during the card read. Nothing was charged and they
    // know why, so no error alert.
    if (result.cancelled) return;

    if (result.readerFailure) {
      // The saved reader is gone from the account: let them pick another
      // rather than retry something that cannot succeed.
      if (result.readerFailure === "not_found" && hasMultipleReaders) {
        setPickerNotice(result.error);
        setShowPicker(true);
        return;
      }
      setReaderFailure({
        failure: result.readerFailure,
        error: result.error,
      });
      return;
    }

    setPaymentError({
      error: result.error,
      cardDeclined: result.cardDeclined,
    });
  };

  // Manual card payment handler
  const handleManualCardPayment = async () => {
    if (!stripe || !elements) {
      setPaymentError({
        error: "Card entry is not ready. Please try again.",
        cardDeclined: false,
      });
      return;
    }

    if (form.values.amount === null || form.values.amount <= 0) {
      form.validateField("amount");
      return;
    }

    setPaymentError(null);
    setIsProcessingManual(true);

    try {
      // Create payment intent
      const response = await authenticatedFetch(
        "/api/stripe/manual-payment-intent",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: user.uid,
            amount: form.values.amount,
            description: job.jobId
              ? `Payment for Job #${job.jobId}`
              : "Job payment",
            processingFee: form.values.processingFee,
            processingFeeUsed: selectedProcessingFee,
            stripeAccountId: user.settings.stripeAccountId,
            currency: job.client.currency.code,
            qbInvoiceId: null,
            // Job payment data (same as terminal payment)
            jobUid: job.uid,
            jobNum: job.jobId,
            client: job.client,
            ...(job.contract?.hasPaymentPlan ? { outOfBandPayment: true } : {}),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await readJson(response);
        throw new Error(errorData.error || "Failed to create payment intent");
      }

      const { clientSecret } = await readJson(response);

      // Create payment method from card element
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error("Card element not found");
      }

      const { error: pmError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
        });

      if (pmError || !paymentMethod) {
        throw new Error(pmError?.message || "Failed to create payment method");
      }

      // Confirm payment on backend
      const confirmResponse = await authenticatedFetch(
        "/api/stripe/confirm-manual-payment",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            paymentMethodId: paymentMethod.id,
            clientSecret,
            stripeAccountId: user.settings.stripeAccountId,
          }),
        }
      );

      const confirmData = await readJson(confirmResponse);

      if (!confirmResponse.ok || !confirmData.success) {
        throw {
          message: confirmData.error || "Payment failed",
          code: confirmData.code,
          decline_code: confirmData.decline_code,
          cardDeclined: confirmData.cardDeclined,
        };
      }

      // `?? null` added 25 Oct 2026 as a precaution, same guard as the
      // terminal path above (Critters Creation Taxidermy, job #17). This
      // manual card path never failed, but if the confirm route ever omits
      // stripeTransactionId, undefined would fail the job write after the
      // card is charged. null is safe, refunds go by stripePaymentIntentId.
      // Full story in useTerminalPayment, above the latestCharge extraction.
      const stripeTransactionId = confirmData.stripeTransactionId ?? null;
      const paymentIntentId = confirmData.paymentIntentId;

      const manualPayment: JobPayment = {
        ...form.values,
        date: Timestamp.fromDate(form.values.date as Date),
        stripePaymentIntentId: paymentIntentId,
        stripeTransactionId,
      };

      const data = processJobPayment(
        user,
        job,
        manualPayment,
        selectedProcessingFee || null
      );

      if (setIsCollectingInitialPayment !== undefined) {
        setIsCollectingInitialPayment(true);
      }

      // Clear card form
      cardElement.clear();
      setManualCardState({ complete: false, error: null });

      onSubmit(data);
    } catch (error: any) {
      const isDecline = error.code === "card_declined" || error.decline_code;
      const mappedMessage = isDecline
        ? STRIPE_CARD_DECLINE_MESSAGES[error.decline_code]
        : undefined;

      setPaymentError({
        error: isDecline
          ? mappedMessage || error.message
          : error?.message || "Failed to process payment",
        cardDeclined: isDecline ? true : false,
      });
    } finally {
      setIsProcessingManual(false);
    }
  };

  // The footer only talks about the reader when the reader tab is the one
  // that will charge.
  const readerBranch =
    paymentsEnabled && enableCardCharging && activeTab === "reader";

  const content = (
    <Stack gap="lg">
      {/* Header Section */}
      {mode === "traditional" && (
        <Stack gap="xs">
          {job.jobId && !withoutJobTitle ? (
            <Text size="lg">Payment for Job #{job.jobId}</Text>
          ) : null}

          <Stack gap={0}>
            <Text size="sm" c="dimmed">
              Payment status:{" "}
              <Text span fw={500}>
                {paymentStatusLabelOverride || job.paymentStatus}
              </Text>
            </Text>

            <Text size="sm" c="dimmed">
              Remaining balance:{" "}
              <Text span fw={500}>
                {formatCurrency(
                  remainingBalanceCustomerFacing,
                  job.client.currency
                )}
              </Text>
            </Text>
          </Stack>
        </Stack>
      )}

      {/* Payment Amount Section */}
      <Stack gap="md">
        <NumberInput
          size="lg"
          label="Payment Amount"
          decimalScale={2}
          min={0}
          max={remainingBalanceCustomerFacing}
          disabled={
            mode === "initial" ||
            (job.paymentStatus === PaymentStatus.PartiallyPaid ||
            job.paymentStatus === PaymentStatus.DepositReceived
              ? false
              : true)
          }
          {...form.getInputProps("amount")}
        />

        <Collapse in={isOutsideOfDeposit}>
          <Alert color="orange" variant="light">
            <Text size="sm">
              The entered amount differs from the deposit amount. This will be
              recorded as a partial payment.
            </Text>
          </Alert>
        </Collapse>
      </Stack>

      {/* Payment Method & Processing Section */}
      <Stack gap="md">
        <Select
          label="Payment Method"
          data={
            mode === "traditional"
              ? [
                  "Credit Card",
                  "Debit Card",
                  "Cash",
                  "Check",
                  "Bank Transfer",
                  "Venmo",
                  "PayPal",
                  "Other",
                ]
              : ["Credit Card", "Debit Card", "Cash", "Check"]
          }
          size="md"
          value={form.values.tender}
          onChange={(value) => form.setFieldValue("tender", value)}
          comboboxProps={{ zIndex: POPOVER_Z_INDEX }}
        />

        {/* Card Payment Options */}
        {paymentsEnabled &&
          (form.values.tender === "Credit Card" ||
            form.values.tender === "Debit Card") && (
            <Box pos="relative">
              {isMobile ? (
                /* Phones: a row of its own, evenly between Payment Method and
                   the card box. Floated over the tabs it covered "Manual
                   Entry". */
                <Group justify="flex-end" mb="lg">
                  <Switch
                    label="Charge Card"
                    labelPosition="left"
                    checked={enableCardCharging}
                    onChange={(event) => {
                      setEnableCardCharging(event.currentTarget.checked);
                    }}
                  />
                </Group>
              ) : (
                <Switch
                  style={{
                    position: "absolute",
                    right: "15px",
                    top: "15px",
                    zIndex: 1000000,
                  }}
                  ml="auto"
                  label="Charge Card"
                  checked={enableCardCharging}
                  onChange={(event) => {
                    setEnableCardCharging(event.currentTarget.checked);
                  }}
                />
              )}

              <Paper
                withBorder
                p="sm"
                radius="sm"
                style={{
                  backgroundColor: !enableCardCharging
                    ? colorScheme === "dark"
                      ? "#2e2e2e"
                      : "var(--mantine-color-gray-0)"
                    : "initial",

                  opacity: !enableCardCharging ? 0.6 : 1,
                }}
              >
                <Tabs
                  styles={{
                    tab: {
                      pointerEvents: !enableCardCharging ? "none" : "auto",
                    },
                  }}
                  value={activeTab}
                  onChange={(value) => {
                    userPickedTab.current = true;
                    setActiveTab(value as "reader" | "manual");
                  }}
                >
                  <Tabs.List>
                    {/* Reachable whenever a reader is registered on the
                        account. The reader is only connected when Process
                        is pressed, so there is no live state to gate on. */}
                    <Tabs.Tab value="reader" disabled={readers.length === 0}>
                      Card Reader
                    </Tabs.Tab>
                    <Tabs.Tab value="manual">Manual Entry</Tabs.Tab>
                  </Tabs.List>

                  <Tabs.Panel value="reader" pt="md">
                    <Stack gap="sm">
                      {pickerOpen ? (
                        <ReaderPicker
                          notice={pickerNotice}
                          onPick={() => {
                            setShowPicker(false);
                            setPickerNotice(null);
                            // A failure from the previous reader has nothing
                            // to do with the one just picked.
                            setReaderFailure(null);
                          }}
                        />
                      ) : isTerminalReady ? (
                        <ReaderSummary
                          disabled={isProcessing}
                          onChange={() => {
                            setShowPicker(true);
                            setReaderFailure(null);
                          }}
                        />
                      ) : (
                        <Alert color="red" variant="light">
                          <Text size="sm">
                            {readers.length > 0
                              ? readerFailureMessage("not_initialized")
                              : readerFailureMessage("no_reader")}
                          </Text>
                        </Alert>
                      )}

                      {showFxDisclosure &&
                        (() => {
                          const fxTotalInJobCurrency = calculateAddition(
                            form.values.amount || 0,
                            form.values.processingFee || 0
                          );
                          return (
                            <Alert
                              color="yellow"
                              variant="light"
                              title={`Card payments charge in ${merchantCurrency}`}
                            >
                              <Stack gap="xs">
                                {previewFxRate && fxTotalInJobCurrency > 0 ? (
                                  <Group gap="xs" wrap="nowrap">
                                    <Text size="sm" fw={600}>
                                      {formatCurrency(
                                        fxTotalInJobCurrency,
                                        job.client.currency
                                      )}
                                    </Text>
                                    <Text size="sm">→</Text>
                                    <Text size="sm" fw={600}>
                                      {formatCurrency(
                                        Number(
                                          (
                                            fxTotalInJobCurrency *
                                            previewFxRate
                                          ).toFixed(2)
                                        ),
                                        {
                                          code: merchantCurrency,
                                          symbol: "$",
                                        }
                                      )}
                                    </Text>
                                    <Text size="sm">
                                      (rate {previewFxRate.toFixed(4)})
                                    </Text>
                                  </Group>
                                ) : previewFxError ? (
                                  <Text size="sm" c="red">
                                    Could not fetch FX rate: {previewFxError}
                                  </Text>
                                ) : (
                                  <Text size="sm">
                                    Fetching today&apos;s rate…
                                  </Text>
                                )}
                                {form.values.processingFee &&
                                form.values.processingFee > 0 ? (
                                  <Text size="xs">
                                    Includes processing fee of{" "}
                                    {formatCurrency(
                                      form.values.processingFee,
                                      job.client.currency
                                    )}
                                    .
                                  </Text>
                                ) : null}
                                <Text size="sm">
                                  Customer&apos;s bank or card may add a fx
                                  fee on top.
                                </Text>
                              </Stack>
                            </Alert>
                          );
                        })()}
                    </Stack>
                  </Tabs.Panel>

                  <Tabs.Panel value="manual" pt="md">
                    <Stack gap="sm">
                      <Stack gap="xs">
                        <CardElement
                          onChange={(event) => {
                            setManualCardState({
                              complete: event.complete,
                              error: event.error ? event.error.message : null,
                            });
                          }}
                          options={{
                            disabled: !enableCardCharging,
                            hidePostalCode: true,
                            disableLink: true,
                            style: {
                              base: {
                                fontSize: "14px",
                                color: "var(--mantine-color-text)",
                                "::placeholder": {
                                  color: "var(--mantine-color-placeholder)",
                                },
                              },
                            },
                          }}
                        />

                        {manualCardState.error && (
                          <Text size="xs" c="red">
                            {manualCardState.error}
                          </Text>
                        )}
                      </Stack>
                    </Stack>
                  </Tabs.Panel>
                </Tabs>
              </Paper>
            </Box>
          )}

        {/* Processing Fee Selection */}
        {!job.lineItems.some(
          (item) => isServiceLineItem(item) && item.notes.includes("OVER_ALL")
        ) &&
          user.settings.processingFees.length > 0 &&
          (form.values.tender === "Credit Card" ||
            form.values.tender === "Debit Card") && (
            <Select
              label="Processing Fee"
              data={
                user.settings.processingFees
                  .filter(
                    (fee) => !fee?.name?.toLowerCase()?.includes("portal")
                  )
                  .map((fee) => ({
                    value: fee.name,
                    label:
                      fee.name && fee.name.toLowerCase() !== "processing fee"
                        ? fee.name.split("Processing Fee - ")[1] || fee.name
                        : fee.name || "Unknown Fee",
                  })) || []
              }
              allowDeselect
              comboboxProps={{ zIndex: POPOVER_Z_INDEX }}
              placeholder="No processing fee"
              onChange={(value) => {
                if (!value) {
                  form.setFieldValue("processingFee", null);
                  setSelectedProcessingFee(null);
                  return;
                }

                const processingFee = user.settings.processingFees.find(
                  (fee) => fee.name === value
                );

                if (processingFee) {
                  form.setFieldValue("processingFee", processingFee.rate);
                } else {
                  form.setFieldValue("processingFee", null);
                }
                setSelectedProcessingFee(processingFee);
              }}
              size="md"
              value={selectedProcessingFee?.name || null}
            />
          )}
      </Stack>

      {/* Payment Summary */}
      <Collapse in={form.values.processingFee && form.values.amount > 0}>
        <Paper withBorder>
          <Alert variant="light" p="md" radius="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Payment Summary
              </Text>
              <Divider />

              <Group justify="space-between">
                <Text size="sm">Payment Amount:</Text>
                <Text size="sm">
                  {formatCurrency(form.values.amount, job.client.currency)}
                </Text>
              </Group>

              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Processing Fee ({selectedProcessingFee?.rate}%
                  {selectedProcessingFee?.fixed &&
                    ` + ${formatCurrency(
                      selectedProcessingFee?.fixed,
                      job.client.currency
                    )}`}
                  ):
                </Text>
                <Text size="sm" c="dimmed">
                  {formatCurrency(
                    form.values.processingFee,
                    job.client.currency
                  )}
                </Text>
              </Group>

              {isGreaterThan(form.values.tax, 0) && (
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Tax ({job.taxRate}%):
                  </Text>
                  <Text size="sm" c="dimmed">
                    {formatCurrency(form.values.tax, job.client.currency)}
                  </Text>
                </Group>
              )}

              <Divider />
              <Group justify="space-between">
                <Text fw={500}>Total to Charge:</Text>
                <Text fw={500} size="lg">
                  {formatCurrency(form.values.total, job.client.currency)}
                </Text>
              </Group>
            </Stack>
          </Alert>
        </Paper>
      </Collapse>

      {/* Payment Details Section */}
      <Stack gap="md">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              disabled={true}
              size="md"
              label="Payment Type"
              {...form.getInputProps("type")}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <DatePickerInput
              size="md"
              clearable={false}
              disabled={
                (enableCardCharging &&
                  (form.values.tender === "Credit Card" ||
                    form.values.tender === "Debit Card")) ||
                mode === "initial"
              }
              w="100%"
              type="default"
              maxDate={new Date()}
              defaultDate={new Date()}
              label="Date Payment Received"
              popoverProps={{ zIndex: POPOVER_Z_INDEX }}
              placeholder="Pick the date"
              value={form.values.date as Date}
              onChange={(newDate) => {
                if (newDate) {
                  // Preserve current time when date changes
                  const currentTime = form.values.date as Date;
                  const dateWithTime = new Date(
                    newDate.getFullYear(),
                    newDate.getMonth(),
                    newDate.getDate(),
                    currentTime.getHours(),
                    currentTime.getMinutes(),
                    currentTime.getSeconds(),
                    currentTime.getMilliseconds()
                  );
                  form.setFieldValue("date", dateWithTime);
                }
              }}
            />
          </Grid.Col>
        </Grid>

        <Textarea
          resize="vertical"
          label="Notes"
          placeholder="Enter any additional notes about the payment"
          size="md"
          rows={3}
          {...form.getInputProps("notes")}
        />
      </Stack>
    </Stack>
  );

  return (
    <form
      style={
        fillHeight
          ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }
          : undefined
      }
      onSubmit={form.onSubmit((newPayment) => {
        const data = processJobPayment(
          user,
          job,
          {
            ...newPayment,
            date: Timestamp.fromDate(newPayment.date as Date),
          },
          selectedProcessingFee || null
        );

        if (setIsCollectingInitialPayment !== undefined) {
          setIsCollectingInitialPayment(true);
        }

        onSubmit(data);
      })}
    >
      {mode === "initial" ? (
        content
      ) : fillHeight ? (
        // overflowX hidden: Grid's negative gutter margins otherwise add a
        // horizontal scrollbar under the form.
        <Box
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {content}
        </Box>
      ) : (
        <ScrollArea.Autosize mah="calc(75dvh - 30px)" scrollbars="y">
          {content}
        </ScrollArea.Autosize>
      )}

      <Stack
        gap="sm"
        mt={fillHeight ? "md" : "xl"}
        style={
          fillHeight
            ? { paddingBottom: "env(safe-area-inset-bottom)" }
            : undefined
        }
      >

        {readerBranch && !isProcessing && readerFailure && (
          <ReaderFailureNotice
            failure={readerFailure.failure}
            message={readerFailure.error}
          />
        )}

        {/* Next to the buttons, never scrolled out of sight: a declined card
            says why (expired, wrong PIN...) so staff can tell the customer. */}
        {paymentError && !isProcessing && (
          <PaymentNotice
            title={paymentError.cardDeclined ? "Card declined" : "Payment failed"}
            message={paymentError.error}
            onClose={() => setPaymentError(null)}
          />
        )}

        <Group justify="space-between" align="center">
          {!isProcessing && mode !== "initial" && (
            <Button variant="default" size="md" onClick={onCancel} flex={1}>
              Cancel
            </Button>
          )}

          {paymentsEnabled && enableCardCharging ? (
            activeTab === "manual" ? (
              <Button
                flex={1}
                onClick={handleManualCardPayment}
                loading={isProcessingManual || isFinalizing}
                disabled={
                  !form.isValid() ||
                  form.values.amount === null ||
                  form.values.amount <= 0 ||
                  !manualCardState.complete ||
                  !!manualCardState.error
                }
                size="md"
                leftSection={<IconCreditCardPay />}
              >
                Process
              </Button>
            ) : isTerminalReady ? (
              <>
                {/* Stays in place for the whole charge so the layout does not
                    jump. Live while the reader is being taken or waits for a
                    card; once the card is being charged it cannot stop. */}
                {isProcessing && (
                  <Button
                    flex={1}
                    variant="light"
                    color="red"
                    size="md"
                    disabled={!canCancel}
                    onClick={cancelTerminalPayment}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={() => runTerminalPayment()}
                  loading={isProcessing || isFinalizing}
                  disabled={!canRunReaderPayment}
                  size="md"
                  flex={1}
                  leftSection={<IconCreditCardPay />}
                >
                  Process
                </Button>
              </>
            ) : (
              <Button
                flex={1}
                size="md"
                disabled
                leftSection={<IconCreditCardPay />}
              >
                {pickerOpen
                  ? "Choose a reader to charge"
                  : readers.length > 0
                  ? "Card reader not ready"
                  : "Set up a reader to charge"}
              </Button>
            )
          ) : (
            <Button
              flex={1}
              type="submit"
              size="md"
              disabled={!form.isValid() || form.values.amount === null}
              loading={
                (isCollectingInitialPayment !== undefined
                  ? isCollectingInitialPayment
                  : false) || isFinalizing
              }
            >
              Log Payment
            </Button>
          )}
        </Group>
      </Stack>
    </form>
  );
};

const PaymentEntryForm = (props: PaymentEntryFormProps) => {
  const user = useUserContext();

  const stripePromiseWithAccount = useMemo(() => {
    if (user?.settings?.stripeAccountId) {
      return loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!, {
        stripeAccount: user.settings.stripeAccountId,
      });
    }
    return getPlatformStripePromise();
  }, [user?.settings?.stripeAccountId]);

  return (
    <Elements stripe={stripePromiseWithAccount}>
      <PaymentEntryFormInner {...props} />
    </Elements>
  );
};

export default PaymentEntryForm;
